import React, { useEffect } from 'react';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  canonical?: string;
  robots?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Peluqueria.AI - Agenda inteligente para peluquerías y barberías',
  description = 'Gestiona citas por teléfono las 24 horas, con recordatorios, confirmaciones y cancelaciones automáticas. La atención por WhatsApp llegará próximamente.',
  keywords = 'peluquería, barbería, citas, agenda, teléfono, automatización, clientes, whatsapp próximamente',
  image = 'https://peluqueria.centraldereservas.ai/og.png',
  url = 'https://peluqueria.centraldereservas.ai',
  type = 'website',
  author = 'Peluqueria.AI',
  canonical,
  robots = 'index,follow',
}) => {
  const fullUrl = canonical || url;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateCanonical = (href: string) => {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', robots);
    updateMetaTag('language', 'Spanish');
    updateMetaTag('revisit-after', '7 days');
    updateCanonical(fullUrl);

    // Open Graph tags
    updatePropertyMetaTag('og:type', type);
    updatePropertyMetaTag('og:url', fullUrl);
    updatePropertyMetaTag('og:title', title);
    updatePropertyMetaTag('og:description', description);
    updatePropertyMetaTag('og:image', image);
    updatePropertyMetaTag('og:site_name', 'Peluqueria.AI');
    updatePropertyMetaTag('og:locale', 'es_ES');

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', fullUrl);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Mobile meta tags
    updateMetaTag('theme-color', '#F7F3EB');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    updateMetaTag('apple-mobile-web-app-title', 'Peluqueria.AI');

    // Add structured data
    const addStructuredData = (id: string, data: object) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        document.head.appendChild(script);
      }
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
    };

    // Organization structured data
    addStructuredData('seo-organization-schema', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Peluqueria.AI',
      url: 'https://peluqueria.centraldereservas.ai',
      logo: 'https://peluqueria.centraldereservas.ai/favicon-512.png',
      description: 'Agenda inteligente para peluquerías y barberías',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'ES',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: 'Spanish',
      },
    });

    // Service structured data
    addStructuredData('seo-service-schema', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Gestión de citas para peluquerías y barberías',
      provider: {
        '@type': 'Organization',
        name: 'Peluqueria.AI',
      },
      description:
        'Recepción automatizada para gestionar citas de peluquería y barbería por teléfono. La atención por WhatsApp estará disponible próximamente.',
      areaServed: 'Spain',
      serviceType: 'Software as a Service',
    });

  }, [
    title,
    description,
    keywords,
    author,
    image,
    url,
    type,
    canonical,
    robots,
    fullUrl,
  ]);

  return null;
};
